import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { HelpCircle, ExternalLink, RefreshCw } from "lucide-react";
import useMediaQuery from "../../../hooks/useMediaQuery";
import { clearAppBadgeOnFocus } from "../../../utils/appBadge";
import usePullToRefresh from "../../../hooks/usePullToRefresh";
import { NAV_GROUPS } from "../navigation";
import "../panel.css";

const THEME_COLOR = "#ffffff"; // = fondo de la barra superior; la barra del navegador en el cel toma este color

/**
 * Shell del panel del negocio: barra superior (marca + ayuda + ver agenda), sidebar
 * agrupado en escritorio y, en móvil, una fila de chips desplazable pegada a la barra
 * superior (todas las secciones visibles, la activa siempre a la vista, borde
 * difuminado hacia donde hay más). Solo se monta UNA de las dos navegaciones (media
 * query en JS) para que el ancla data-tour="nav.tabs" apunte siempre a la visible.
 *
 * `items`: [{ id, label, short?, icon, group, disabled?, tour? }] ya filtrados por rol.
 */
export default function PanelShell({ brand, items, activeTab, onSelect, onHelp, onViewAgenda, onRefresh, children }) {
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const ptr = usePullToRefresh(onRefresh);

  // El badge del ícono (push sin abrir) se limpia al abrir o volver al panel.
  useEffect(() => clearAppBadgeOnFocus(), []);

  // theme-color claro mientras el panel está montado (index.html trae el azul de la landing).
  useEffect(() => {
    let meta = document.querySelector('meta[name="theme-color"]');
    const created = !meta;
    if (!meta) { meta = document.createElement("meta"); meta.name = "theme-color"; document.head.appendChild(meta); }
    const prev = meta.content;
    meta.content = THEME_COLOR;
    return () => { if (created) meta.remove(); else meta.content = prev; };
  }, []);

  const renderItem = (it) => {
    const Icon = it.icon;
    return (
      <button
        key={it.id}
        type="button"
        className={`cp-navitem ${activeTab === it.id ? "is-active" : ""}`}
        onClick={() => onSelect(it.id)}
        disabled={it.disabled}
        aria-current={activeTab === it.id ? "page" : undefined}
        data-tour={it.tour}
      >
        <Icon size={18} /> {it.label}
      </button>
    );
  };

  return (
    <div className="cp-root">
      <header className="cp-topbar" translate="no">
        <div className="cp-topbar-inner">
          <div className="cp-brand">
            <span className="cp-brand-icon">{brand.icon}</span>
            <span className="cp-brand-name">{brand.name}</span>
          </div>
          <div className="cp-topbar-actions">
            {onViewAgenda && (
              <button type="button" className="cp-btn" onClick={onViewAgenda} title="Ver mi agenda como cliente" aria-label="Ver mi agenda como cliente">
                <ExternalLink size={16} /> <span className="cp-btn-label">Ver mi agenda</span>
              </button>
            )}
            <button
              type="button"
              className="cp-btn"
              onClick={onHelp}
              title="¿Qué hay en esta sección?"
              aria-label="Recorrido guiado de esta sección"
              data-tour="nav.help"
            >
              <HelpCircle size={18} /> <span className="cp-btn-label">Ayuda</span>
            </button>
          </div>
        </div>
        {!isDesktop && <ChipsNav items={items} activeTab={activeTab} onSelect={onSelect} />}
      </header>

      {/* Indicador de "jalar para recargar": crece con el dedo y gira mientras recarga. */}
      {onRefresh && (
        <div className={`cp-ptr ${ptr.dragging ? "is-dragging" : ""} ${ptr.refreshing ? "is-refreshing" : ""}`} style={{ height: ptr.pull }} aria-hidden>
          <RefreshCw size={20} style={{ transform: `rotate(${ptr.progress * 360}deg)`, opacity: 0.35 + ptr.progress * 0.65 }} />
        </div>
      )}
      <div className="cp-layout">
        {isDesktop && (
          <aside className="cp-sidebar">
            <nav aria-label="Secciones" data-tour="nav.tabs">
              {NAV_GROUPS.map((g) => {
                const groupItems = items.filter((it) => it.group === g.id);
                if (!groupItems.length) return null;
                return (
                  <div key={g.id} className="cp-sidebar-group">
                    {g.label && <p className="cp-eyebrow">{g.label}</p>}
                    {groupItems.map(renderItem)}
                  </div>
                );
              })}
            </nav>
          </aside>
        )}
        <main className="cp-main">{children}</main>
      </div>
    </div>
  );
}

/**
 * Chips desplazables (móvil). El relleno del chip activo no salta: una copia de la
 * fila, pintada ya como activa, va encima recortada con clip-path al chip actual; al
 * cambiar de sección se anima el recorte y el color del texto hace crossfade solo.
 * El chip activo se trae al centro al cambiar (scroll manual: scrollIntoView en
 * móvil también movería la página).
 */
function ChipsNav({ items, activeTab, onSelect }) {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const [clip, setClip] = useState(null);
  const [fade, setFade] = useState("right"); // hacia dónde hay más contenido: right | left | both | none

  const updateFade = () => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 4;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setFade(left && right ? "both" : left ? "left" : right ? "right" : "none");
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const track = trackRef.current;
    const active = track?.querySelector(".cp-chip.is-active");
    if (el && track && active) {
      const right = track.offsetWidth - (active.offsetLeft + active.offsetWidth);
      setClip(`inset(0 ${right}px 0 ${active.offsetLeft}px round 999px)`);
      const target = track.offsetLeft + active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2;
      el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
    } else {
      setClip(null);
    }
    updateFade();
  }, [activeTab, items]);

  useEffect(() => {
    window.addEventListener("resize", updateFade);
    return () => window.removeEventListener("resize", updateFade);
  }, []);

  const renderChips = (ghost) => items.map((it) => {
    const Icon = it.icon;
    const active = activeTab === it.id;
    return (
      <button
        key={it.id}
        type="button"
        className={`cp-chip ${active ? "is-active" : ""}`}
        onClick={ghost ? undefined : () => onSelect(it.id)}
        disabled={it.disabled}
        tabIndex={ghost ? -1 : undefined}
        aria-current={!ghost && active ? "page" : undefined}
        data-tour={ghost ? undefined : it.tour}
      >
        <Icon size={16} /> {it.short || it.label}
      </button>
    );
  });

  return (
    <nav className="cp-chips" aria-label="Secciones" data-tour="nav.tabs" data-fade={fade}>
      <div className="cp-chips-scroll" ref={scrollRef} onScroll={updateFade}>
        <div className="cp-chips-track" ref={trackRef}>
          {renderChips(false)}
          <div className="cp-chips-ghost" aria-hidden style={{ clipPath: clip || "inset(0 100% 0 0 round 999px)" }}>
            {renderChips(true)}
          </div>
        </div>
      </div>
    </nav>
  );
}
